import type { CollectionFieldPath, Filter, GlobalPrimitiveFieldId, GlobalSingleRelationFieldId, ResolvedMonospacePrimitiveField, ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import type { SupportedOperatorsSpec } from './filter-operators';
import type { FilterConfigResolvableField } from './resolve-filter-config';
import { cloneDeep } from 'lodash-es';
import { createFieldFilter, FilterOperator } from '#layers/data-model/public';
import { getDefaultOperatorForField, narrowOperators } from './filter-operators';

const filterActionBrand = Symbol('FilterAction');

/**
 * A filter a control offers to add. Only the resolver constructs one, after the resolving owner's
 * gate accepted its exact clause; the owner inserting it checks the clause against its own gate.
 */
export interface FilterAction {
	readonly [filterActionBrand]: true;
	/** Whether the inserted filter opens for editing its value. */
	readonly autoAdvance: boolean;
	/** A fresh copy of the clause for each insertion. */
	create: () => Filter;
}

interface LogicalFilterPrimitiveField extends Pick<ResolvedMonospacePrimitiveField, 'apiName'>, FilterConfigResolvableField {}
type LogicalFilterRelationField = Pick<ResolvedMonospaceSingleRelationField, 'apiName'>;

export interface FilterActionOptions {
	findPrimitiveFieldById?: (id: GlobalPrimitiveFieldId) => LogicalFilterPrimitiveField | undefined;
	findSingleRelationFieldById?: (id: GlobalSingleRelationFieldId) => LogicalFilterRelationField | undefined;
	/**
	 * Resolves the narrowing spec for a primitive field's filter operators, so the seeded operator
	 * matches what FilterSimple shows once the field is rendered. Omit to seed from the field type's
	 * operators.
	 */
	supportedOperatorsFor?: (field: LogicalFilterPrimitiveField) => SupportedOperatorsSpec | undefined;
	/**
	 * Resolves a field type's preferred seeded operator (e.g. datetime seeds `_lt`), applied when it
	 * is among the narrowed operators. Omit to seed the first of them.
	 */
	defaultOperatorFor?: (field: LogicalFilterPrimitiveField) => FilterOperator | undefined;
	/** Whether the owner's filter gate accepts a clause. Omit to accept every clause. */
	allowsClause?: (clause: Filter) => boolean;
}

export function createFilterActionResolver(options: FilterActionOptions) {
	function allows(clause: Filter) {
		return options.allowsClause?.(clause) ?? true;
	}

	function action(clause: Filter, autoAdvance: boolean): FilterAction | undefined {
		if (!allows(clause)) return;
		return { [filterActionBrand]: true, autoAdvance, create: () => cloneDeep(clause) };
	}

	function toApiPath(fieldPath: CollectionFieldPath): string[] | undefined {
		const apiPath: string[] = [];

		for (const id of fieldPath) {
			const field = options.findPrimitiveFieldById?.(id as GlobalPrimitiveFieldId)
				?? options.findSingleRelationFieldById?.(id as GlobalSingleRelationFieldId);

			if (!field) {
				console.error(`[createFilterActionResolver]: Cannot create filter - field not found for ID '${id}'`);
				return undefined;
			}

			apiPath.push(field.apiName);
		}

		return apiPath;
	}

	/** Adds a filter on `fieldPath` with an operator the gate accepts and no value yet. */
	function resolve(fieldPath: CollectionFieldPath): FilterAction | undefined {
		const apiPath = toApiPath(fieldPath);
		if (!apiPath) return;

		const lastFieldId = fieldPath.at(-1);
		const field = lastFieldId ? options.findPrimitiveFieldById?.(lastFieldId as GlobalPrimitiveFieldId) : undefined;
		const configured = field && options.supportedOperatorsFor?.(field);
		const accepted: SupportedOperatorsSpec = (operators) => narrowOperators(operators, configured)
			.filter((operator) => allows(createFieldFilter(apiPath, operator)));
		const operator = getDefaultOperatorForField(field, accepted, field && options.defaultOperatorFor?.(field));
		if (operator === undefined) return;

		// Using undefined (not null) avoids collision with intentional "is null" filters.
		return action(createFieldFilter(apiPath, operator), true);
	}

	/** Adds a filter matching `value` on `fieldPath`: equality, or `_null` for an empty value. */
	function resolveWithValue(fieldPath: CollectionFieldPath, value: unknown): FilterAction | undefined {
		const apiPath = toApiPath(fieldPath);
		if (!apiPath) return;

		const clause = value == null
			? createFieldFilter(apiPath, FilterOperator.NULL, true)
			: createFieldFilter(apiPath, FilterOperator.EQ, value);
		return action(clause, false);
	}

	return { resolve, resolveWithValue };
}
